import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { 
  LayoutDashboard, Users, Flag, ScrollText, LogOut, Shield,
  ChevronLeft, Settings, Bell, Calendar, BadgeCheck, Crown, Image, BarChart3, MessageSquare
} from 'lucide-react';

const AdminLayout = () => {
  const { admin, logout, hasPermission } = useAdmin();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: null },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analytics', permission: 'analytics:read' },
    { to: '/admin/users', icon: Users, label: 'Users', permission: 'users:read' },
    { to: '/admin/reports', icon: Flag, label: 'Reports', permission: 'reports:read' },
    { to: '/admin/moderation', icon: Image, label: 'Photo Moderation', permission: 'reports:read' },
    { to: '/admin/contact', icon: MessageSquare, label: 'Contact/Feedback', permission: 'reports:read' },
    { to: '/admin/events', icon: Calendar, label: 'Events', permission: 'broadcasts:write' },
    { to: '/admin/verifications', icon: BadgeCheck, label: 'Verifications', permission: 'users:read' },
    { to: '/admin/memberships', icon: Crown, label: 'Memberships', permission: 'users:read' },
    { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs', permission: 'audit:read' },
  ];

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-[#FF2E63] text-white';
      case 'admin': return 'bg-blue-500 text-white';
      case 'moderator': return 'bg-yellow-500 text-black';
      case 'support': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#111] border-r border-white/10 flex flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF2E63]/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#FF2E63]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">PULSE</h1>
              <p className="text-xs text-[#A1A1AA]">Admin Console</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            if (item.permission && !hasPermission(item.permission)) return null;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-[#FF2E63] text-white' 
                      : 'text-[#A1A1AA] hover:bg-white/5 hover:text-white'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <span className="text-white font-semibold">
                {admin?.username?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{admin?.username}</p>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(admin?.role)}`}>
                {admin?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/10 px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-[#A1A1AA] hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-white">
                <Bell className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
